import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useReduxAjax, RequestMethod, type ReduxAjaxParams } from '@chipp972/redux-ajax';
import { useSelector } from 'react-redux';
import { t } from 'i18next';

import type { SearchResult, UseFetchSearchResultParams } from './search.types';
import { LevelType } from '../ilink.type';
import { buildUrl, useBuildHeader } from '../../utils/apis/RestApi';
import { type BreadcrumbLevel, resetProduct, updateCatalogSearch } from '../../store/catalogSlice';
import { useAppDispatch } from '../../store/store';
import { addFromTraceability, initInterventionState } from '../../store/interventionSlice';
import { deleteCustomer } from '../../store/customerSlice';
import { selectCountryId, selectStoreId } from '../../store/selectors/userInfo';
import { type CatalogProductRaw } from '../masterdata/masterdata.type';
import { selectCatalogSearch } from '../../store/selectors/catalog';
import { getAppRoute, RoutesPath } from '../../core/routes';
import { clearBasket } from '../../store/basketSlice';

export const useSearchFoundDispatch = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const catalogSearch = useSelector(selectCatalogSearch);

  const handleProductFound = (catalogProduct: CatalogProductRaw) => {
    const { product, productInformation } = catalogProduct;

    if (!productInformation) return;

    const updatedBreadcrumb: BreadcrumbLevel[] = [
      { ...catalogSearch.breadcrumb[0], hide: true },
      { level: LevelType.categories, label: productInformation.process.name, id: productInformation.process.id, hide: true },
      { level: LevelType.products, label: productInformation.category.name, id: productInformation.category.id, hide: true },
      { level: LevelType.product, label: t('catalog.breadcrumb.search.product') },
    ];

    dispatch(
      updateCatalogSearch({
        ...catalogSearch,
        product: {
          ...product,
          id: product.id?.toString(),
          purchaseDate: product.purchaseDate ? new Date(product.purchaseDate) : undefined,
          process: { id: productInformation.process.id, label: productInformation.process.name },
          category: { id: productInformation.category.id, label: productInformation.category.name },
        },
        breadcrumb: updatedBreadcrumb,
        fetchNeeded: true,
      })
    );

    dispatch(clearBasket());
    dispatch(initInterventionState());
  };

  const handleInterventionFound = (interventionNumber: string) => {
    const interventionRoute = getAppRoute(RoutesPath.interventionPage, interventionNumber);

    if (interventionRoute === pathname) {
      return;
    }

    dispatch(resetProduct());
    dispatch(deleteCustomer());
    dispatch(addFromTraceability());
  };

  return { handleProductFound, handleInterventionFound };
};

export const useFetchSearchResult = ({ requestType }: UseFetchSearchResultParams) => {
  const countryId = useSelector(selectCountryId);
  const storeId = useSelector(selectStoreId);
  const headers = useBuildHeader({ fullLocale: true });
  const request = useReduxAjax<SearchResult | null>(`${requestType}-request`);
  const result = request.response;

  const fetchResult = useCallback(
    (input: string) => {
      const url = buildUrl('ICARE_BACK', `search/${input}?countryId=${countryId}&storeId=${storeId}`);
      const requestContent: ReduxAjaxParams = {
        url,
        headers,
        method: RequestMethod.get,
      };

      request.submitRequest({ requestContent });
    },
    [countryId, storeId]
  );

  return {
    result,
    fetchResult,
    isRequestPending: request.isRequestPending,
    isRequestFailed: request.isRequestFailed,
    resetResponse: request.resetResponse,
    resetRequest: request.resetRequest,
  };
};
